import React, { useState } from "react";
import type { ExtraCategoryType, ExtraType } from "../../../../../../../common-deprecated/types/CommonTypes";
import { BnbLabel } from "../../../../utils/labels";
import * as Type from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Type";
import * as Button from "../../../../../../../common-deprecated/styles/v2/lexus/components/Button";
import Extra from "./Extra";
import { find } from "../../../../../../../common-deprecated/utils";
import * as Flex from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Flex";
import * as PackStyles from "../../step-customize/main-content/styles/Pack";
import * as CategorizedExtrasStyles from "./styles/CategorizedExtras";
import Icon from "../../../../../../../common-deprecated/styles/v2/lexus/globals/Icon";
import { IconWrapper } from "../../../../../../../common-deprecated/styles/v2/lexus/globals/IconWrapper";
import { useBnbSelector } from "../../../../redux/store";
import { trackExtrasShow } from "../../../../utils/tracking";
import CarDataConsolidator from "../../../../../../shared-logic/utils/classes/CarDataConsolidator";
import { getCategorizedExtrasCode } from "../../../../utils/extrasUtils";

type CategorizedExtrasType = {
    categorizedExtra: ExtraCategoryType;
    onAddExtra?: (extra: ExtraType, optionId?: string) => void;
    onRemoveExtra: (extra: ExtraType) => void;
    onSetExtraOption?: (extra: ExtraType, extraOptionId: string) => void;
    basketExtras: ExtraType[];
    alwaysShowAllExtras?: boolean;
    handleClickOnlyOnIcon?: boolean;
    allowRemoving?: boolean;
    disableBuildAccessories?: boolean;
};

const CategorizedExtras = (props: CategorizedExtrasType): JSX.Element => {
    const {
        categorizedExtra,
        basketExtras,
        onAddExtra,
        onRemoveExtra,
        onSetExtraOption,
        alwaysShowAllExtras,
        handleClickOnlyOnIcon,
        allowRemoving,
        disableBuildAccessories,
    } = props;
    const { name, extras, code } = categorizedExtra;

    const step = useBnbSelector((state) => state.navigation.step);
    const items = useBnbSelector((state) => state.items);
    const basket = useBnbSelector((state) => state.basket);
    const standardPacks = items.packs.filter((pack) => pack.standard);

    const [showAllExtras, setShowAllExtras] = useState<boolean>(!!alwaysShowAllExtras);
    const extrasFromPacks = CarDataConsolidator.getExtrasFromPacks(items.packs, true, false);
    const extrasOnlyFromPacks = CarDataConsolidator.getExtrasFromPacks(items.packs).filter(
        (packs) => !find(packs.id, items.extras),
    );
    const allPacks = [...basket.packs, ...standardPacks];

    // Standard packs are not added to the basket but are in fact selected. This is why they are spread below
    const extrasFromSelectedPacks = CarDataConsolidator.getExtrasFromPacks(allPacks, true, false);
    const optionalExtrasFromSelectedPacks = CarDataConsolidator.getExtrasFromPacks(allPacks, true, false);

    const maxItems = 6;

    const codeKey = getCategorizedExtrasCode(code);

    return (
        <>
            <CategorizedExtrasStyles.Header>
                <Flex.Row>
                    <Flex.Col width={{ _: 1, md: 3 / 4 }}>
                        <Type.SubMicroText>{name}</Type.SubMicroText>
                    </Flex.Col>
                    <Flex.Col width={{ _: 1, md: 1 / 4 }} display="flex" justifyContent="flex-end">
                        <CategorizedExtrasStyles.ResultCount>
                            <BnbLabel label="extrasCount" params={{ count: extras.length }} />
                        </CategorizedExtrasStyles.ResultCount>
                    </Flex.Col>
                </Flex.Row>
            </CategorizedExtrasStyles.Header>
            <PackStyles.Grid>
                {extras.slice(0, showAllExtras ? extras.length : maxItems).map((extra) => {
                    const basketExtra = find(extra.id, basketExtras);

                    // The extra is in pack when it is a standard extra in any pack and not an optional extra in a selected pack
                    const isInPack =
                        !!find(extra.id, extrasFromPacks) && !find(extra.id, optionalExtrasFromSelectedPacks);
                    // The extra is already in a selected pack
                    const isInSelectedPack =
                        !!find(extra.id, extrasFromSelectedPacks) || !!find(extra.id, basket.packExtras);
                    // The extra is only available in a pack and not as separate extra
                    const isOnlyInPack = !!find(extra.id, extrasOnlyFromPacks);

                    return (
                        <Extra
                            key={extra.id}
                            extra={extra}
                            // When the extra is selected in a pack the not equipped icon is shown disabled
                            equipped={!!basketExtra && !isInSelectedPack}
                            equippedOptionId={basketExtra?.selectedOptionId || ""}
                            onSelect={(optionId) => {
                                if (basketExtra) onRemoveExtra(extra);
                                else if (onAddExtra) onAddExtra(extra, optionId);
                            }}
                            onOptionSelect={(optionId) =>
                                onSetExtraOption ? onSetExtraOption(extra, optionId) : undefined
                            }
                            optional
                            enabled={!disableBuildAccessories}
                            handleClickOnlyOnIcon={handleClickOnlyOnIcon}
                            allowRemoving={allowRemoving}
                            background
                            isInPack={isInPack}
                            isInSelectedPack={isInSelectedPack}
                            isOnlyInPack={isOnlyInPack}
                        />
                    );
                })}
            </PackStyles.Grid>
            {!alwaysShowAllExtras && extras.length > maxItems && !showAllExtras && (
                <Flex.Row my={2}>
                    <Flex.Col width={1} display="flex" justifyContent="center">
                        <Button.ViewMore
                            m={0}
                            onClick={() => {
                                setShowAllExtras(true);
                                trackExtrasShow(step, codeKey.tracking);
                            }}
                            aria-labelledby={codeKey.button}
                            aria-expanded={showAllExtras}
                        >
                            <span id={codeKey.button}>
                                <BnbLabel label={codeKey.label} />
                            </span>
                            <IconWrapper aria-hidden="true" ml={1} iconColor="azure">
                                <Icon variant="chevronDown" />
                            </IconWrapper>
                        </Button.ViewMore>
                    </Flex.Col>
                </Flex.Row>
            )}
        </>
    );
};

export default CategorizedExtras;
